import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, timeAgo, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { KpiCard, PageHeader, StatusPill, PriorityPill } from "@/components/shared/Common";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import {
  ClipboardText, CheckCircle, Warning, Clock, Users, Gauge, Timer, Fire, Broadcast, ArrowUpRight
} from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [prod, setProd] = useState([]);
  const [qual, setQual] = useState([]);
  const [util, setUtil] = useState([]);
  const [assigns, setAssigns] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [top, setTop] = useState([]);

  useEffect(() => {
    (async () => {
      const [k, p, q, u, a, up, t] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/analytics/productivity-trend"),
        api.get("/analytics/quality-trend"),
        api.get("/analytics/team-utilization"),
        api.get("/dashboard/recent-assignments"),
        api.get("/dashboard/upcoming-sla"),
        api.get("/analytics/leaderboard"),
      ]);
      setKpis(k.data);
      setProd(p.data.items);
      setQual(q.data.items);
      setUtil(u.data.items);
      setAssigns(a.data.items);
      setUpcoming(up.data.items);
      setTop(t.data.items.slice(0, 5));
    })().catch(() => {});
  }, []);

  const isLeader = user?.role === "leadership";
  const chartColors = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

  return (
    <div>
      <PageHeader
        title={isLeader ? "Executive Overview" : user?.role === "associate" ? "Your Workspace" : "Operations Command"}
        subtitle={`Welcome, ${user?.name}. Real-time workforce intelligence.`}
      >
        {user?.role !== "associate" && (
          <>
            <Button data-testid="dashboard-new-case-btn" onClick={() => nav("/cases/new")} className="h-9">
              + New Case
            </Button>
            <Button data-testid="dashboard-allocate-btn" variant="outline" onClick={() => nav("/allocation")} className="h-9">
              <Broadcast size={16} className="mr-1" /> Allocation Engine
            </Button>
          </>
        )}
      </PageHeader>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testId="kpi-total-cases" label="Total cases" value={kpis?.total_cases ?? "—"} sub="All-time" icon={ClipboardText} />
        <KpiCard testId="kpi-pending" label="In queue" value={kpis?.pending ?? "—"} sub="Awaiting allocation" icon={Clock} accent="text-amber-500" />
        <KpiCard testId="kpi-in-progress" label="In progress" value={kpis?.in_progress ?? "—"} sub="Active work" icon={Timer} accent="text-sky-500" />
        <KpiCard testId="kpi-completed" label="Completed today" value={kpis?.completed_today ?? "—"} sub="24h window" icon={CheckCircle} accent="text-emerald-500" />
        <KpiCard testId="kpi-escalated" label="Escalated" value={kpis?.escalated ?? "—"} sub="Need attention" icon={Fire} accent="text-red-500" />
        <KpiCard testId="kpi-overdue" label="Overdue" value={kpis?.overdue ?? "—"} sub="SLA at risk" icon={Warning} accent="text-red-500" />
        <KpiCard testId="kpi-sla" label="SLA compliance" value={`${kpis?.sla_compliance ?? 0}%`} sub="Completed on-time" icon={Gauge} />
        <KpiCard testId="kpi-utilization" label="Utilization" value={`${kpis?.utilization ?? 0}%`} sub={`${kpis?.available_employees ?? 0} available`} icon={Users} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 border rounded-md p-5 bg-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow text-muted-foreground">Productivity trend</div>
              <div className="text-sm font-medium">Cases assigned vs completed · last 14 days</div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={prod}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" fontSize={11} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <YAxis fontSize={11} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="assigned" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="completed" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border rounded-md p-5 bg-card">
          <div className="eyebrow text-muted-foreground mb-1">Quality distribution</div>
          <div className="text-sm font-medium mb-4">Workforce quality bands</div>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={qual} dataKey="count" nameKey="bucket" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {qual.map((_, i) => <Cell key={i} fill={chartColors[i % chartColors.length]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Team utilization + Top performers + Recent + Upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border rounded-md p-5 bg-card">
          <div className="eyebrow text-muted-foreground">Team utilization</div>
          <div className="text-sm font-medium mb-4">Workload by team</div>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={util}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="team" fontSize={10} tick={{ fill: "hsl(var(--muted-foreground))" }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis fontSize={11} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="workload" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border rounded-md p-5 bg-card">
          <div className="eyebrow text-muted-foreground">Top performers</div>
          <div className="text-sm font-medium mb-4">Leaderboard</div>
          <div className="space-y-2" data-testid="top-performers">
            {top.map((p, i) => (
              <div key={p.id} className="flex items-center justify-between text-sm border-b last:border-b-0 py-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-6 text-xs mono text-muted-foreground">#{i + 1}</div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{p.team}</div>
                  </div>
                </div>
                <div className="text-xs mono">{p.completed} cases</div>
              </div>
            ))}
          </div>
        </div>

        <div className="border rounded-md p-5 bg-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow text-muted-foreground">Upcoming SLA breaches</div>
              <div className="text-sm font-medium">Next 12 hours</div>
            </div>
            <Warning className="text-amber-500" size={18} />
          </div>
          <div className="space-y-2">
            {upcoming.length === 0 && <div className="text-sm text-muted-foreground">All clear.</div>}
            {upcoming.map(c => (
              <div key={c.id} onClick={() => nav(`/cases/${c.id}`)} className="flex items-center justify-between p-2 rounded hover:bg-secondary/50 cursor-pointer">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate mono">{c.case_id}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{c.customer}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <PriorityPill priority={c.priority} />
                  <div className="text-[10px] mono text-muted-foreground">{formatDate(c.due_date)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="border rounded-md mt-6 bg-card">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <div className="eyebrow text-muted-foreground">Live activity</div>
            <div className="text-sm font-medium">Recent allocations</div>
          </div>
          <Button data-testid="view-all-allocation-btn" variant="ghost" size="sm" onClick={() => nav("/allocation")}>
            View all <ArrowUpRight size={14} className="ml-1" />
          </Button>
        </div>
        <div className="divide-y" data-testid="recent-allocations">
          {assigns.slice(0, 6).map(a => (
            <div key={a.id} className="p-4 flex items-center justify-between text-sm">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <div className="min-w-0">
                  <div className="font-medium truncate">Assigned to {a.assignee_name}</div>
                  <div className="text-xs text-muted-foreground mono">by {a.assigned_by === "auto" ? "AUTO" : "MANUAL"} · score {a.score?.toFixed?.(2) ?? "—"}</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground mono">{timeAgo(a.assigned_at)}</div>
            </div>
          ))}
          {assigns.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No allocations yet</div>}
        </div>
      </div>
    </div>
  );
}
